/**
 * FadingScrollView — Horizontal ScrollView with edge fade overlays.
 *
 * Fades only appear when the content is wider than the visible area.
 */
import { useState, useCallback } from 'react'
import { type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native'
import { styled, XStack, useTheme, type GetProps } from 'tamagui'
import { ScrollView } from '../layout'
import { LinearGradient } from 'expo-linear-gradient'

const FadingScrollViewFrame = styled(XStack, {
  name: 'FadingScrollView',
})

type FrameProps = GetProps<typeof FadingScrollViewFrame>

interface FadingScrollViewProps extends FrameProps {
  /** Width of the fade overlays (px). Typically matches the padding being counteracted. */
  fadeWidth: number
  /** Override fade color. Defaults to the current theme's background. */
  fadeColor?: string
  /** Props forwarded to the inner ScrollView. */
  scrollViewProps?: Omit<GetProps<typeof ScrollView>, 'horizontal' | 'children'>
  children?: React.ReactNode
}

export const FadingScrollView = FadingScrollViewFrame.styleable<FadingScrollViewProps>(
  ({ fadeWidth, fadeColor, scrollViewProps, children, ...props }, ref) => {
    const theme = useTheme()
    const color = fadeColor ?? theme.background.val

    const [contentWidth, setContentWidth] = useState(0)
    const [containerWidth, setContainerWidth] = useState(0)
    const [scrollX, setScrollX] = useState(0)

    const overflows = contentWidth > containerWidth
    const showLeft = fadeWidth > 0 && overflows && scrollX > 1
    const showRight = fadeWidth > 0 && overflows && scrollX < contentWidth - containerWidth - 1

    const handleContentSizeChange = useCallback((w: number) => {
      setContentWidth(w)
    }, [])

    const handleLayout = useCallback((e: { nativeEvent: { layout: { width: number } } }) => {
      setContainerWidth(e.nativeEvent.layout.width)
    }, [])

    const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
      setScrollX(e.nativeEvent.contentOffset.x)
    }, [])

    return (
      <FadingScrollViewFrame ref={ref} {...props}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          onContentSizeChange={handleContentSizeChange}
          onLayout={handleLayout}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          {...scrollViewProps}
        >
          {children}
        </ScrollView>

        {showLeft && (
          <LinearGradient
            colors={[color, 'transparent']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: fadeWidth,
            }}
            pointerEvents="none"
          />
        )}

        {showRight && (
          <LinearGradient
            colors={['transparent', color]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: fadeWidth,
            }}
            pointerEvents="none"
          />
        )}
      </FadingScrollViewFrame>
    )
  },
)
